import React, { useContext } from "react";
import { motion } from "framer-motion";
import { CurrentPlaylistContext } from "../contexts/CurrentPlaylistContext";
import { PlayerContext } from "../contexts/PlayerContext";
import { FavouritesContext } from "../contexts/FavouritesContext";

const MiniPlayer = ({ onExpand }) => {
  const { activePlaylist, activeVideoIndex, activeVideos, setActiveVideoIndex, artists } =
    useContext(CurrentPlaylistContext);
  const { isFavourite, toggleFavourite } = useContext(FavouritesContext);

  const {
    isPlaying,
    togglePlay,
    currentTime,
    duration,
    formattedTime,
    formattedDuration,
    seekByPercentage,
    volume,
    isMuted,
    setVolumeLevel,
    toggleMute,
    playerRef,
  } = useContext(PlayerContext);

  // Don't render if nothing is playing
  if (activePlaylist === null || activeVideos.length === 0) {
    return null;
  }

  const currentVideo = activeVideos[activeVideoIndex];
  if (!currentVideo) return null;

  // Get current artist for the logo
  const currentArtist = artists.find((artist) => artist.id === activePlaylist);

  // Load the logo image using require (same as ArtistTile)
  let artistLogoSrc = null;
  if (currentArtist?.logo) {
    try {
      artistLogoSrc = require(`../assets/playlists/${currentArtist.logo}`);
    } catch (e) {
      console.warn("Could not load artist logo:", currentArtist.logo);
    }
  }

  const playNextVideo = () => {
    // Calculate next video index
    const nextIndex = (activeVideoIndex + 1) % activeVideos.length;
    const nextVideo = activeVideos[nextIndex];

    if (!nextVideo || !playerRef.current) {
      setActiveVideoIndex(nextIndex);
      return;
    }

    // Use loadVideoById to maintain user gesture for mobile autoplay
    if (playerRef.current.loadVideoById) {
      console.log("MiniPlayer: Loading next video with autoplay");
      playerRef.current.loadVideoById({
        videoId: nextVideo.video_id,
        startSeconds: 0
      });
    }
    setActiveVideoIndex(nextIndex);
  };

  const playPreviousVideo = () => {
    // Calculate previous video index
    const prevIndex = (activeVideoIndex - 1 + activeVideos.length) % activeVideos.length;
    const prevVideo = activeVideos[prevIndex];

    if (!prevVideo || !playerRef.current) {
      setActiveVideoIndex(prevIndex);
      return;
    }

    // Use loadVideoById to maintain user gesture for mobile autoplay
    if (playerRef.current.loadVideoById) {
      console.log("MiniPlayer: Loading previous video with autoplay");
      playerRef.current.loadVideoById({
        videoId: prevVideo.video_id,
        startSeconds: 0
      });
    }
    setActiveVideoIndex(prevIndex);
  };

  const handleProgressBarClick = (event) => {
    const rect = event.target.getBoundingClientRect();
    const clickPositionInPixels = event.clientX - rect.left;
    const percentage = (clickPositionInPixels / rect.width) * 100;
    seekByPercentage(percentage);
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleVolumeChange = (event) => {
    const newVolume = parseInt(event.target.value, 10);
    setVolumeLevel(newVolume);
  };

  const getVolumeSliderStyle = () => {
    const percentage = isMuted ? 0 : volume;
    return {
      background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${percentage}%, #e0e0e0 ${percentage}%, #e0e0e0 100%)`,
    };
  };

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) {
      return "fa-volume-xmark";
    } else if (volume < 50) {
      return "fa-volume-low";
    } else {
      return "fa-volume-high";
    }
  };

  return (
    <motion.div
      className="mini-player"
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mini-player-content">
        {/* Controls with progress bar - centered (moves to top on mobile) */}
        <div className="mini-player-center">
          <div className="mini-player-controls">
            <i
              className="fa-solid fa-backward-step mini-control"
              onClick={playPreviousVideo}
            ></i>
            {isPlaying ? (
              <i
                className="fa-solid fa-circle-pause mini-control-main"
                onClick={togglePlay}
              ></i>
            ) : (
              <i
                className="fa-solid fa-circle-play mini-control-main"
                onClick={togglePlay}
              ></i>
            )}
            <i
              className="fa-solid fa-forward-step mini-control"
              onClick={playNextVideo}
            ></i>
          </div>

          {/* Progress bar and time under controls */}
          <div className="mini-player-progress-wrapper">
            <span className="mini-player-time-current">{formattedTime}</span>
            <div className="mini-player-progress" onClick={handleProgressBarClick}>
              <div
                className="mini-player-progress-fill"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span className="mini-player-time-duration">{formattedDuration}</span>
          </div>
        </div>

        {/* Bottom row: logo and song info (on mobile, this moves below controls) */}
        <div className="mini-player-bottom-row">
          {/* Artist logo */}
          <div className="mini-player-logo" onClick={onExpand}>
            {artistLogoSrc ? (
              <img src={artistLogoSrc} alt={currentArtist?.name || "Artist"} />
            ) : (
              <i className="fa-solid fa-music"></i>
            )}
          </div>

          {/* Song info */}
          <div className="mini-player-info" onClick={onExpand}>
            <div className="mini-player-title">{currentVideo.title}</div>
            <div className="mini-player-artist">{currentVideo.artist}</div>
          </div>
        </div>

        {/* Favourite and note icons */}
        <div className="mini-player-actions">
          <i
            className={`favourite-btn-player fa-heart ${isFavourite(currentVideo?.video_id) ? "fa-solid active" : "fa-regular"}`}
            onClick={() => toggleFavourite(currentVideo)}
            title={isFavourite(currentVideo?.video_id) ? "Odebrat z oblíbených" : "Přidat do oblíbených"}
          ></i>
          <a
            href={currentVideo.lyrics_url || `https://zpevnik.proscholy.cz/hledani?text=${currentVideo.title.replace(/\s+/g, "_")}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Najít text písně"
          >
            <i className="fa-solid fa-music mini-player-action-icon"></i>
          </a>
        </div>

        {/* Volume control */}
        <div className="mini-player-volume">
          <i
            className={`fa-solid ${getVolumeIcon()} mini-volume-icon`}
            onClick={toggleMute}
          ></i>
          <input
            type="range"
            min="0"
            max="100"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="mini-volume-slider"
            style={getVolumeSliderStyle()}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default MiniPlayer;
